import React, { useState } from 'react';
import { Controller } from 'react-hook-form-new';
import {
  Alert,
  Checkbox,
  Divider,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  FormControl,
  FormControlLabel,
  Stack,
} from '@nextiva/next-ui-components';
import { useGetTCRStateOptions } from 'hooks/useGetTCRStateOptions';
import { isValidUrl, isValidEmail, isTCRAprovedEmail } from 'utils/validators';
import { useGetVertical } from 'hooks/SMS/useGetVertical';
import { InfoDialogButton } from '../InformationDialog/InformationDialogButton';
import {
  HelpTooltip,
  OverlineTypo,
  SubtitleTypo,
  StyledLinkBox,
  Body2Typo,
} from '../WizardV3.styled';
import { CompanyTypeDialogButton } from '../CustomDialog/CustomDialog';
import { useWizardContextV3 } from '../WizardContextV3/WizardContextV3';
import {
  formatPhoneNumber,
  validatePhoneNumber,
} from '../WizardContextV3/customValidations';
import {
  CompanyTypeInfo,
  MenuProps,
  BRAND_REGISTRATION_FIELDS,
  FIELD_NAMES,
} from '../constants';
import GoogleAutoCompleteInput from './GoogleAutoCompleteInput';
import { StockExchangeSelect } from './StockExchangeSelectV3';

const dataTestId = 'PlatformAdminUI-BrandRegistration';

export function BrandRegistration() {
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    watch,
    handleResetForm,
    handleSubmit,
    handleNextStep,
    declinedWithBrandVerified,
  } = useWizardContextV3();
  const isPublicCompany =
    getValues(BRAND_REGISTRATION_FIELDS.COMPANY_TYPE) ===
    CompanyTypeInfo[1].value;
  const isSoleProprietor =
    getValues(BRAND_REGISTRATION_FIELDS.COMPANY_TYPE) ===
    CompanyTypeInfo[4].value;

  const businessAddressTooltip = isSoleProprietor
    ? 'Please enter the address of your company.'
    : 'Please enter the address of your company as listed in tax information.';

  const [disableBrandName, setDisableBrandName] = useState(
    !!watch(BRAND_REGISTRATION_FIELDS.IS_SAME_AS_COMPANY_NAME),
  );
  const [disableBusinessNumber, setDisableBusinessNumber] = useState(
    !!watch(BRAND_REGISTRATION_FIELDS.IS_SAME_AS_BUSINESS_NUMBER),
  );
  const { data: stateValues } = useGetTCRStateOptions();
  const getVerticalQuery = useGetVertical();
  const verticalMapData = getVerticalQuery?.data;

  return (
    <form onSubmit={handleSubmit(handleNextStep)} data-testid={`${dataTestId}`}>
      <Stack
        data-testid={`${dataTestId}-Stack`}
        alignContent="center"
        alignItems="center"
        sx={{ padding: '0px 0px 0px 0px' }}
      >
        <Grid
          container
          direction="row"
          columnSpacing={4}
          rowSpacing={2}
          alignItems="flex-end"
          marginBottom="32px"
        >
          {declinedWithBrandVerified && (
            <Grid item xs={12}>
              <Alert
                severity="info"
                fullWidth
                data-testid={`${dataTestId}-DeclinedWithBrandVerified-InfoAlert`}
              >
                Your brand is registered and verified. If you need to change any
                information regarding your brand, please contact our Amazing
                Service team directly at 888-287-9457 or&nbsp;
                <Link
                  href="https://help.nextiva.com/s/contactsupport?_ics=1708990322657&irclickid=%7E53X1069726978Z68-agmd67418Z0ULJMLBvmnded5ZPMFvusqgd6"
                  underline="none"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${dataTestId}-DeclinedWithBrandVerified-SubmitCaseLink`}
                >
                  submit a case
                </Link>
                .
              </Alert>
            </Grid>
          )}
          <Grid item xs={5}>
            <InputLabel id="company-type-label">
              <OverlineTypo>
                {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.COMPANY_TYPE]} *
              </OverlineTypo>
            </InputLabel>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.COMPANY_TYPE}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  data-testid={`${dataTestId}-CompanyType-Select`}
                  disabled
                  id="companyType-id"
                  fullWidth
                  labelId="company-type-label"
                  MenuProps={MenuProps}
                  onChange={onChange}
                  onBlur={onBlur}
                  required
                  value={value || ''}
                >
                  {CompanyTypeInfo.map((companyInfo) => (
                    <MenuItem key={companyInfo.value} value={companyInfo.value}>
                      {companyInfo.title}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Grid>
          <CompanyTypeDialogButton
            disable={declinedWithBrandVerified}
            handleResetForm={handleResetForm}
          />
        </Grid>
        <Grid
          container
          direction="row"
          columnSpacing={4}
          rowSpacing={2}
          marginTop="0px"
        >
          <Grid item xs={5}>
            <InputLabel id="business-type-label">
              <OverlineTypo>
                {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.BUSINESS_TYPE]} *
                <HelpTooltip
                  dataTestId={`${dataTestId}-BusinessType-Tooltip`}
                  title="Please select a sector of your company’s business in industry."
                ></HelpTooltip>
              </OverlineTypo>
            </InputLabel>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.BUSINESS_TYPE}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  data-testid={`${dataTestId}-BusinessType-Select`}
                  id="business-type"
                  fullWidth
                  labelId="business-type-label"
                  placeholder="Select industry"
                  required
                  disabled={declinedWithBrandVerified}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value || ''}
                  MenuProps={MenuProps}
                  error={!!errors.typeOfBusiness}
                >
                  {verticalMapData &&
                    Object.keys(verticalMapData).map((key) => (
                      <MenuItem key={key} value={key}>
                        {verticalMapData[key].displayName}
                      </MenuItem>
                    ))}
                </Select>
              )}
            />
          </Grid>
        </Grid>

        <Grid
          container
          direction="row"
          rowSpacing={4}
          margin="16px 0px 32px 0px"
        >
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        {!isSoleProprietor && (
          <Grid container direction="row" columnSpacing={4} rowSpacing={2}>
            <Grid item xs={12}>
              <SubtitleTypo>
                Provide TAX ID/EIN
                <InfoDialogButton
                  dataTestId={`${dataTestId}-TaxIdInfo-InfoDialogButton`}
                  title="Tax ID/EIN"
                  content={
                    <StyledLinkBox>
                      Please enter your 9 digit EIN/Tax ID. This is used by the
                      Campaign Registry to verify your brand per the CTIA
                      (Cellular Telecommunications Industry Association). For
                      more details on why this information is needed visit:
                      <strong>
                        <a
                          href="https://www.nextiva.com/support/articles/sms-faqs.html"
                          rel="noreferrer"
                          target="_blank"
                          data-testid={`${dataTestId}-TaxIdInfo-FaqLink`}
                        >
                          {' '}
                          SMS registration and FAQs.{' '}
                        </a>
                      </strong>
                      If you don&apos;t have EIN or Tax ID, you will need to
                      obtain it first, before proceeding.
                    </StyledLinkBox>
                  }
                />
              </SubtitleTypo>
            </Grid>
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.TAX_ID}
                rules={{
                  required: !declinedWithBrandVerified,
                  pattern: /^[0-9]+$/,
                  maxLength: 9,
                  minLength: 9,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    id="tax-id"
                    data-testid={`${dataTestId}-TaxId-TextField`}
                    placeholder={
                      declinedWithBrandVerified
                        ? '*********'
                        : 'Enter Tax ID/EIN'
                    }
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ''}
                    error={!!errors.taxIdEin}
                    disabled={declinedWithBrandVerified}
                    helperText={
                      !!errors.taxIdEin &&
                      'Please enter a valid TAX ID/EIN number.'
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.TAX_ID_CONFIRM}
                rules={{
                  required: !declinedWithBrandVerified,
                  validate: (value) => {
                    const { taxIdEin } = getValues();
                    return value === taxIdEin;
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    id="tax-id-confirmation"
                    data-testid={`${dataTestId}-TaxIdConfirmation-TextField`}
                    value={value || ''}
                    placeholder={
                      declinedWithBrandVerified
                        ? '*********'
                        : 'Re-enter Tax ID/EIN'
                    }
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.taxIdEinConfirm}
                    disabled={declinedWithBrandVerified}
                    helperText={
                      !!errors.taxIdEinConfirm && 'TAX ID/EIN doesn’t match.'
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Body2Typo>
                Tax ID/EIN will be added to your account information or replace
                existing record.
              </Body2Typo>
            </Grid>
          </Grid>
        )}
        {!isSoleProprietor && (
          <Grid
            container
            direction="row"
            rowSpacing={4}
            margin="16px 0px 32px 0px"
          >
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        )}
        {isSoleProprietor && (
          <Grid
            container
            direction="row"
            columnSpacing={4}
            rowSpacing={2}
            marginBottom="32px"
          >
            <Grid item xs={12}>
              <SubtitleTypo>Provide personal information</SubtitleTypo>
            </Grid>
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.FIRST_NAME}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <TextField
                      id="firstName-id"
                      data-testid={`${dataTestId}-FirstName-TextField`}
                      label={
                        <OverlineTypo>
                          {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.FIRST_NAME]} *
                        </OverlineTypo>
                      }
                      placeholder="Enter first name"
                      value={value || ''}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.firstName}
                      helperText={
                        !!errors.firstName && 'Please enter your first name.'
                      }
                    />
                  );
                }}
              />
            </Grid>
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.LAST_NAME}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <TextField
                      id="lastName-id"
                      data-testid={`${dataTestId}-LastName-TextField`}
                      label={
                        <OverlineTypo>
                          {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.LAST_NAME]} *
                        </OverlineTypo>
                      }
                      placeholder="Enter last name"
                      value={value || ''}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.lastName}
                      helperText={
                        !!errors.lastName && 'Please enter your last name.'
                      }
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        )}
        {isSoleProprietor && (
          <Grid
            container
            direction="row"
            columnSpacing={4}
            rowSpacing={2}
            marginBottom="32px"
          >
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.MOBILE_PHONE_NUMBER}
                rules={{
                  required: true,
                  validate: validatePhoneNumber,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    id="mobilePhoneNumber-id"
                    data-testid={`${dataTestId}-mobilePhoneNumber-TextField`}
                    label={
                      <OverlineTypo>
                        {
                          FIELD_NAMES[
                            BRAND_REGISTRATION_FIELDS.MOBILE_PHONE_NUMBER
                          ]
                        }{' '}
                        *
                      </OverlineTypo>
                    }
                    placeholder="(000) 000-0000"
                    value={value || ''}
                    fullWidth
                    onChange={(e) => {
                      onChange(formatPhoneNumber(e.target.value));
                    }}
                    onBlur={onBlur}
                    error={!!errors.mobilePhoneNumber}
                    helperText={
                      !!errors.mobilePhoneNumber &&
                      'Please enter a valid phone number.'
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Body2Typo>
                Phone number verification is mandatory for Sole Proprietor
                Entity. You will receive a verification message within 24 hours
                after submitting the registration application. Note, this cannot
                be your Nextiva number.
              </Body2Typo>
            </Grid>
          </Grid>
        )}
        {isSoleProprietor && (
          <Grid
            container
            direction="row"
            columnSpacing={4}
            rowSpacing={2}
            marginTop="0px"
          >
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.EMAIL}
                rules={{
                  required: true,
                  validate: isValidEmail,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    id="email-address-id"
                    data-testid={`${dataTestId}-EmailAddress-TextField`}
                    label={
                      <OverlineTypo>
                        {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.EMAIL]} *
                      </OverlineTypo>
                    }
                    placeholder="name@example.com"
                    value={value || ''}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.email}
                    helperText={!!errors.email && 'Please enter valid email'}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
        {isSoleProprietor && (
          <Grid container direction="row" rowSpacing={4} margin="16px 0px">
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        )}
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <SubtitleTypo>Tell us about your company</SubtitleTypo>
          </Grid>
          {isPublicCompany && (
            <Grid item xs={12}>
              <Body2Typo>
                <strong>Note: </strong>
                TCR verifies the brand’s identity of public companies. Your
                application will not proceed for registration until brand
                verification is complete.
              </Body2Typo>
            </Grid>
          )}

          {!isSoleProprietor && (
            <Grid item xs={12}>
              <Alert
                severity="info"
                fullWidth
                data-testid={`${dataTestId}-InfoAlert`}
              >
                Please make sure that your company details matches the TAX
                ID/EIN entered above.
              </Alert>
            </Grid>
          )}
        </Grid>
        {!isSoleProprietor && (
          <Grid
            container
            direction="row"
            columnSpacing={4}
            rowSpacing={2}
            margin="0px 0px 32px -32px"
          >
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.COMPANY_LEGAL_NAME}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    id="company-legal-name-id"
                    data-testid={`${dataTestId}-CompanyLegalName-TextField`}
                    label={
                      <OverlineTypo>
                        {
                          FIELD_NAMES[
                            BRAND_REGISTRATION_FIELDS.COMPANY_LEGAL_NAME
                          ]
                        }{' '}
                        *
                        <HelpTooltip
                          dataTestId={`${dataTestId}-CompanyLegalName-Tooltip`}
                          title="Please enter the name of your company as listed in tax information."
                        />
                      </OverlineTypo>
                    }
                    placeholder="Enter company legal name"
                    value={value || ''}
                    fullWidth
                    error={!!errors.companyLegalName}
                    disabled={declinedWithBrandVerified}
                    onChange={(e) => {
                      onChange(e.target.value);
                      if (disableBrandName) {
                        setValue(
                          BRAND_REGISTRATION_FIELDS.BRAND_NAME,
                          e.target.value,
                        );
                      }
                    }}
                    onBlur={onBlur}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
        <Grid
          container
          direction="row"
          columnSpacing={4}
          rowSpacing={2}
          margin="0px 0px 32px -32px"
        >
          {!isSoleProprietor && (
            <Grid item xs={12}>
              <InputLabel id="brandName-label">
                <OverlineTypo>
                  {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.BRAND_NAME]} *
                  <HelpTooltip
                    dataTestId={`${dataTestId}-BrandName-Tooltip`}
                    title='"Doing Business As" - the public name associated with your company or business.'
                  />
                </OverlineTypo>
              </InputLabel>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.IS_SAME_AS_COMPANY_NAME}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    value={value}
                    label={
                      <Body2Typo>
                        {
                          FIELD_NAMES[
                            BRAND_REGISTRATION_FIELDS.IS_SAME_AS_COMPANY_NAME
                          ]
                        }
                      </Body2Typo>
                    }
                    control={
                      <Checkbox
                        checked={!!value}
                        data-testid={`${dataTestId}-BrandName-Checkbox`}
                        disabled={declinedWithBrandVerified}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const companyName = getValues('companyLegalName');
                            setValue('brandName', companyName);
                          }
                          onChange(e.target.checked);
                          setDisableBrandName(e.target.checked);
                        }}
                        sx={{ marginRight: '8px' }}
                      />
                    }
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={5}>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.BRAND_NAME}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  id="brand-name"
                  data-testid={`${dataTestId}-BrandName-TextField`}
                  placeholder="Enter brand name"
                  value={value || ''}
                  fullWidth
                  label={
                    isSoleProprietor ? (
                      <OverlineTypo>
                        {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.BRAND_NAME]} *
                        <HelpTooltip
                          dataTestId={`${dataTestId}-CompanyLegalName-Tooltip`}
                          title='"Doing Business As" - the public name associated with your company or business.'
                        />
                      </OverlineTypo>
                    ) : null
                  }
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors.brandName}
                  disabled={
                    declinedWithBrandVerified ||
                    (!isSoleProprietor && disableBrandName)
                  }
                />
              )}
            />
          </Grid>
        </Grid>
        {isPublicCompany && (
          <Grid
            container
            direction="row"
            columnSpacing={4}
            rowSpacing={2}
            margin="0px 0px 32px -32px"
          >
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.STOCK_SYMBOL}
                rules={{ required: true, maxLength: 10 }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    id="stock-symbol"
                    data-testid={`${dataTestId}-StockSymbol-TextField`}
                    label={
                      <OverlineTypo>
                        {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.STOCK_SYMBOL]} *
                        <HelpTooltip
                          dataTestId={`${dataTestId}-StockSymbol-Tooltip`}
                          title="Please enter the symbol of your company’s shares as enlisted in stock exchange."
                        />
                      </OverlineTypo>
                    }
                    placeholder="Enter symbol"
                    value={value || ''}
                    disabled={declinedWithBrandVerified}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors?.stockSymbol}
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}>
              <InputLabel id="stock-exchange-label">
                <OverlineTypo>
                  {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.STOCK_EXCHANGE]} *
                  <HelpTooltip
                    dataTestId={`${dataTestId}-StockExchange-Tooltip`}
                    title="Please select the stock exchange where your company is registered as a public company."
                  />
                </OverlineTypo>
              </InputLabel>
              <StockExchangeSelect
                control={control}
                disabled={declinedWithBrandVerified}
              />
            </Grid>
          </Grid>
        )}
        <Grid container direction="row" spacing={2} margin="0px 0px 0px -16px">
          <Grid item xs={10}>
            <InputLabel id="business-address-label">
              <OverlineTypo>
                Business address *
                <HelpTooltip
                  dataTestId={`${dataTestId}-BusinessAddress-Tooltip`}
                  title={businessAddressTooltip}
                />
              </OverlineTypo>
            </InputLabel>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.STREET}
              rules={{ required: true }}
              render={({ field: { value = '', onChange } }) => {
                const setValueConfig = {
                  shouldValidate: true,
                  shouldTouch: true,
                };
                function clearAllFields() {
                  setValue(BRAND_REGISTRATION_FIELDS.CITY, '', setValueConfig);
                  setValue(BRAND_REGISTRATION_FIELDS.STATE, '', setValueConfig);
                  setValue(
                    BRAND_REGISTRATION_FIELDS.ZIP_CODE,
                    '',
                    setValueConfig,
                  );
                  setValue(
                    BRAND_REGISTRATION_FIELDS.COUNTRY,
                    '',
                    setValueConfig,
                  );
                }
                const stringValue = value as string; // I don't know why this is being typed wrong.
                return (
                  <GoogleAutoCompleteInput
                    data-testid={`${dataTestId}-Address-GoogleAutoCompleteInput`}
                    value={stringValue}
                    onInputChange={onChange}
                    error={!!errors.addressStreet}
                    disabled={declinedWithBrandVerified}
                    label={
                      <OverlineTypo>
                        {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.STREET]}
                      </OverlineTypo>
                    }
                    clearAllAddressFields={clearAllFields}
                    clearCityStateZipFields={clearAllFields}
                    handleAddressChange={(addressComponents) => {
                      if (!addressComponents) {
                        return;
                      }

                      const streetNumber = addressComponents?.street_number;

                      if (streetNumber) {
                        const addressStreet = `${streetNumber} ${addressComponents?.route}`;
                        const addressState =
                          addressComponents?.administrative_area_level_1;
                        const addressCity = addressComponents?.locality;
                        const addressZipCode = addressComponents?.postal_code;
                        const addressCountry = addressComponents?.country;

                        setValue(
                          'addressStreet',
                          addressStreet,
                          setValueConfig,
                        );
                        setValue(
                          BRAND_REGISTRATION_FIELDS.CITY,
                          addressCity,
                          setValueConfig,
                        );
                        setValue(
                          BRAND_REGISTRATION_FIELDS.STATE,
                          addressState,
                          setValueConfig,
                        );
                        setValue(
                          BRAND_REGISTRATION_FIELDS.ZIP_CODE,
                          addressZipCode,
                          setValueConfig,
                        );
                        setValue(
                          BRAND_REGISTRATION_FIELDS.COUNTRY,
                          addressCountry,
                          setValueConfig,
                        );
                      }
                    }}
                  />
                );
              }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction="row"
          columnSpacing={4}
          rowSpacing={2}
          margin="0px 0px 32px -32px"
        >
          <Grid item xs={5}>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.CITY}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  id="address-city"
                  data-testid={`${dataTestId}-City-TextField`}
                  label={
                    <OverlineTypo>
                      {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.CITY]}
                    </OverlineTypo>
                  }
                  placeholder="Enter city"
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                  disabled={declinedWithBrandVerified}
                  onBlur={onBlur}
                  error={!!errors.addressCity}
                />
              )}
            />
          </Grid>
          <Grid item xs={5}>
            <InputLabel id="state-label">
              <OverlineTypo>
                {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.STATE]}
              </OverlineTypo>
            </InputLabel>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.STATE}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="address-state"
                  data-testid={`${dataTestId}-State-Select`}
                  labelId="state-label"
                  placeholder="Select state"
                  value={value || ''}
                  fullWidth
                  required
                  onChange={onChange}
                  disabled={declinedWithBrandVerified}
                  onBlur={onBlur}
                  MenuProps={MenuProps}
                  error={!!errors.addressState}
                >
                  {stateValues &&
                    stateValues.map((state) => (
                      <MenuItem key={state.value} value={state.value}>
                        {state.displayName}
                      </MenuItem>
                    ))}
                </Select>
              )}
            />
          </Grid>
          <Grid item xs={5}>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.COUNTRY}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  id="address-country-id"
                  data-testid={`${dataTestId}-Country-TextField`}
                  label={
                    <OverlineTypo>
                      {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.COUNTRY]}
                    </OverlineTypo>
                  }
                  placeholder="Enter country"
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                  disabled={declinedWithBrandVerified}
                  onBlur={onBlur}
                  error={!!errors.addressCountry}
                />
              )}
            />
          </Grid>
          <Grid item xs={5}>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.ZIP_CODE}
              rules={{
                required: true,
                pattern: /^\d{5}([- ]\d{4})?$/,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  id="zip-code-id"
                  data-testid={`${dataTestId}-ZipCode-TextField`}
                  label={
                    <OverlineTypo>
                      {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.ZIP_CODE]}
                    </OverlineTypo>
                  }
                  placeholder="Enter ZIP code"
                  value={value || ''}
                  fullWidth
                  error={!!errors.addressZipCode}
                  onChange={onChange}
                  disabled={declinedWithBrandVerified}
                  onBlur={onBlur}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container direction="row" spacing={2} margin="0px 0px 0px -16px">
          <Grid item xs={10}>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.WEBSITE}
              rules={{
                required: true,
                validate: isValidUrl,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  id="company-website"
                  data-testid={`${dataTestId}-CompanyWebsite-TextField`}
                  label={
                    <OverlineTypo>
                      {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.WEBSITE]} *
                      <HelpTooltip
                        dataTestId={`${dataTestId}-CompanyWebsite-Tooltip`}
                        title="Include a link to your website. If you don't have a website, you can include a link to your company's Facebook, Twitter, Instagram, or Etsy page."
                      />
                    </OverlineTypo>
                  }
                  placeholder="www.yourwebsite.com"
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                  disabled={declinedWithBrandVerified}
                  onBlur={onBlur}
                  error={!!errors.webSite}
                  helperText={
                    !!errors.webSite && 'Please enter a valid website link.'
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Body2Typo>
              Please provide link of working website. Maximum 255 characters.
            </Body2Typo>
          </Grid>
        </Grid>

        <Grid
          container
          direction="row"
          rowSpacing={4}
          margin="16px 0px 32px 0px"
        >
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        <Grid
          container
          direction="row"
          columnSpacing={4}
          rowSpacing={2}
          marginBottom="32px"
        >
          <Grid item xs={12}>
            <SubtitleTypo>Provide contact information</SubtitleTypo>
          </Grid>
          <Grid item xs={5}>
            <FormControl variant="outlined" fullWidth>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.PHONE_NUMBER}
                rules={{
                  required: true,
                  validate: validatePhoneNumber,
                }}
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <TextField
                      label={
                        <OverlineTypo>
                          {
                            FIELD_NAMES[
                              BRAND_REGISTRATION_FIELDS.BUSINESS_NUMBER
                            ]
                          }{' '}
                          *
                        </OverlineTypo>
                      }
                      value={value}
                      placeholder="(000) 000-0000"
                      fullWidth
                      disabled={declinedWithBrandVerified}
                      onChange={(e) => {
                        const formattedNumber = formatPhoneNumber(
                          e.target.value,
                        );
                        onChange(formattedNumber);
                        if (disableBusinessNumber) {
                          setValue(
                            BRAND_REGISTRATION_FIELDS.BUSINESS_NUMBER,
                            formattedNumber,
                          );
                        }
                      }}
                      onBlur={onBlur}
                      error={!!errors.phoneNumber}
                      data-testid={`${dataTestId}-BusinessContactNumber-TextField`}
                      helperText={
                        !!errors.phoneNumber &&
                        'Please enter a valid phone number.'
                      }
                    />
                  );
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
        {/* Support Phone Number - Sent to BE as `businessNumber` and used in supportMessage */}
        <Grid
          container
          direction="row"
          columnSpacing={4}
          rowSpacing={2}
          margin="0px 0px 32px -32px"
        >
          <Grid item xs={12}>
            <InputLabel id="phoneNumber-label">
              <OverlineTypo>
                {FIELD_NAMES[BRAND_REGISTRATION_FIELDS.PHONE_NUMBER]} *
              </OverlineTypo>
            </InputLabel>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.IS_SAME_AS_BUSINESS_NUMBER}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  value={value}
                  label={
                    <Body2Typo>
                      {
                        FIELD_NAMES[
                          BRAND_REGISTRATION_FIELDS.IS_SAME_AS_BUSINESS_NUMBER
                        ]
                      }
                    </Body2Typo>
                  }
                  control={
                    <Checkbox
                      checked={!!value}
                      data-testid={`${dataTestId}-phoneNumber-Checkbox`}
                      disabled={declinedWithBrandVerified}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const pn = getValues(
                            BRAND_REGISTRATION_FIELDS.PHONE_NUMBER,
                          );
                          setValue(
                            BRAND_REGISTRATION_FIELDS.BUSINESS_NUMBER,
                            pn,
                          );
                        }
                        onChange(e.target.checked);
                        setDisableBusinessNumber(e.target.checked);
                      }}
                      sx={{ marginRight: '8px' }}
                    />
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={5}>
            <Controller
              control={control}
              name={BRAND_REGISTRATION_FIELDS.BUSINESS_NUMBER}
              rules={{
                required: true,
                validate: validatePhoneNumber,
              }}
              render={({ field: { onChange, onBlur, value } }) => {
                return (
                  <TextField
                    id="businessNumber"
                    data-testid={`${dataTestId}-businessNumber-TextField`}
                    value={value}
                    placeholder="(000) 000-0000"
                    fullWidth
                    onChange={(e) => {
                      onChange(formatPhoneNumber(e.target.value));
                    }}
                    onBlur={onBlur}
                    error={!!errors.businessNumber}
                    helperText={
                      !!errors.businessNumber &&
                      'Please enter a valid phone number.'
                    }
                    disabled={
                      declinedWithBrandVerified || disableBusinessNumber
                    }
                  />
                );
              }}
            />
          </Grid>
        </Grid>
        {isPublicCompany && (
          <Grid
            container
            direction="row"
            columnSpacing={4}
            rowSpacing={2}
            margin="0px 0px 32px -32px"
          >
            <Grid item xs={5}>
              <Controller
                control={control}
                name={BRAND_REGISTRATION_FIELDS.BUSINESS_EMAIL}
                rules={{
                  required: 'Please enter valid email.',
                  validate: isTCRAprovedEmail,
                }}
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <FormControl variant="outlined" fullWidth>
                      <TextField
                        data-testid={`${dataTestId}-BusinessEmail-TextField`}
                        label={
                          <OverlineTypo>
                            {
                              FIELD_NAMES[
                                BRAND_REGISTRATION_FIELDS.BUSINESS_EMAIL
                              ]
                            }{' '}
                            *
                          </OverlineTypo>
                        }
                        placeholder="name@example.com"
                        value={value || ''}
                        onChange={onChange}
                        disabled={declinedWithBrandVerified}
                        onBlur={onBlur}
                        error={!!errors.businessEmail}
                        helperText={errors.businessEmail?.message}
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Body2Typo>
                Business email verification is mandatory for Public company.
                After brand identity is verified, you’ll receive a verification
                email valid for 7 days. If not completed within that time, you
                can request new email from Messaging page. You’ll have total 30
                days to complete the verification by clicking the link in the
                latest verification email. If verification fails or expires, you
                will have 45 days to resubmit your application.
              </Body2Typo>
              <br />
              <Body2Typo>
                <strong>Note: </strong>
                Submissions using personal email address, poorly formatted email
                address, spam-associated email address, distribution list emails
                (e.g., sales@ or support@) or email address from free email
                domains will be rejected. The email must belong to an individual
                employee of the business.
              </Body2Typo>
            </Grid>
          </Grid>
        )}
      </Stack>
    </form>
  );
}
